import { Link } from "react-router-dom";
import { Apple, Play, ShieldCheck, Brain, Phone, Sparkles, ArrowRight, Check, Users, UsersRound, Stethoscope } from "lucide-react";

const APP_STORE_URL = "https://apps.apple.com/us/app/generational-health/id6751965153";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.generationalh.mobile.app&utm_source=na_Med";

import { StoreQRCodes } from "@/components/StoreQRCodes";

export default function Download() {
  return (
    <div data-testid="download-page" className="bg-white">
      <section className="py-20 lg:py-28 bg-gradient-hero relative overflow-hidden" data-testid="download-hero">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-teal/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="eyebrow mb-5">Download</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy">
              Help is <span className="hl-teal">here.</span><br />When you <span className="hl-teal">need it.</span>
            </h1>
            <p className="mt-7 text-lg text-muted-ink leading-relaxed max-w-xl">
              Scan the code, or tap your app store. You'll be matched with support in under two minutes.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none bg-navy hover:bg-navy-light text-white rounded-xl px-5 py-3.5 flex items-center gap-3 transition" data-testid="download-app-store">
                <Apple className="w-7 h-7" />
                <div className="leading-tight text-left">
                  <p className="text-[11px] uppercase tracking-wider text-white/60">Download on</p>
                  <p className="text-base font-bold">App Store</p>
                </div>
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none bg-navy hover:bg-navy-light text-white rounded-xl px-5 py-3.5 flex items-center gap-3 transition" data-testid="download-play-store">
                <Play className="w-7 h-7 fill-white" />
                <div className="leading-tight text-left">
                  <p className="text-[11px] uppercase tracking-wider text-white/60">Get it on</p>
                  <p className="text-base font-bold">Google Play</p>
                </div>
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-ink font-medium">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal" /> HIPAA compliant</span>
              <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-teal" /> AI companion</span>
              <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-teal" /> 24/7 support</span>
            </div>
          </div>

          <div className="relative flex justify-center">
            <div className="absolute -inset-6 bg-teal/20 rounded-[3rem] blur-3xl" aria-hidden="true" />
            <StoreQRCodes eyebrow="Scan to download" footer="Point your phone camera at your store's code." />
          </div>
        </div>
      </section>

      <section className="py-20 bg-cream" data-testid="download-features">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">In the app</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.1]">
              Everything you need — <span className="hl-teal">without the noise.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: <Brain className="w-6 h-6" />, title: "AI Companion", desc: "Available 24/7 for grounding, journaling, and coping support." },
              { icon: <Sparkles className="w-6 h-6" />, title: "Real Providers", desc: "Licensed therapists, counselors, and psychiatrists — insured & verified." },
              { icon: <Phone className="w-6 h-6" />, title: "Crisis Support", desc: "Trained specialists answering in under two minutes, day or night." },
            ].map((f, i) => (
              <div key={i} className="gh-card" data-testid={`download-feature-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center mb-5">
                  {f.icon}
                </div>
                <h3 className="font-display text-xl mb-2 text-navy">{f.title}</h3>
                <p className="text-muted-ink text-[15px] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/features" className="btn-outline-teal" data-testid="download-all-features">
              See every feature <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing clarity */}
      <section className="py-20 bg-white" data-testid="download-pricing">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="eyebrow mb-4">What's free · What's billable</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.1] max-w-3xl mx-auto">
              The app is <span className="hl-teal">free.</span> Professional care is <span className="hl-teal">covered</span> — by insurance or a sliding scale.
            </h2>
            <p className="mt-6 text-lg text-muted-ink max-w-2xl mx-auto">
              We keep the front door wide open. When you're ready for professional clinical services,
              we work with your insurance or meet you where you are financially.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Free column */}
            <div className="rounded-2xl border-2 border-teal/40 bg-teal-light/40 p-8" data-testid="download-pricing-free">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-teal text-white flex items-center justify-center">
                  <Check className="w-6 h-6" strokeWidth={3} />
                </div>
                <div>
                  <p className="eyebrow !text-teal !mb-1">Always free</p>
                  <h3 className="font-display text-2xl text-navy">No cost, ever</h3>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "Download the app (iOS & Android)",
                  "Create an account and onboarding",
                  "24/7 AI Companion (grounding, coping, journaling)",
                  "Crisis support & warm handoff to 988",
                  "Insurance eligibility check via pVerify",
                  "Educational resources & self-guided tools",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-navy">
                    <Check className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-[15px] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Billable column */}
            <div className="rounded-2xl border-2 border-navy/10 bg-cream p-8" data-testid="download-pricing-billable">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <p className="eyebrow !mb-1">Insurance or sliding scale</p>
                  <h3 className="font-display text-2xl text-navy">Professional clinical services</h3>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  { icon: <Stethoscope className="w-5 h-5" />, title: "Mental Health Clinicians", desc: "1:1 sessions with licensed therapists, counselors, and psychiatrists." },
                  { icon: <UsersRound className="w-5 h-5" />, title: "Peer Support Services", desc: "Certified peer specialists with lived recovery experience." },
                  { icon: <Users className="w-5 h-5" />, title: "Group Therapy", desc: "Clinician-led groups for anxiety, addiction, trauma, and more." },
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white text-navy border border-navy/10 flex items-center justify-center flex-shrink-0">
                      {it.icon}
                    </div>
                    <div>
                      <div className="text-navy font-semibold">{it.title}</div>
                      <div className="text-muted-ink text-[15px] leading-relaxed">{it.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-navy/10 text-sm text-muted-ink leading-relaxed">
                Billed to Medicaid, Medicare, and most commercial insurance plans (auto-verified via pVerify).
                Sliding-scale pricing available for the uninsured. Donations fund our access program for those who still can't afford care.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
